import React, { useState, useEffect, useRef } from 'react';
import { NavLink, Link } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import Logo from './Logo';
import MegaMenu from './MegaMenu';
import { articles } from '../../data/articles';

interface MenuItem {
  title: string;
  path: string;
  megaMenu?: {
    sections: {
      title: string;
      items: {
        title: string;
        description: string;
        path: string;
      }[];
    }[];
  };
}

const menuItems: MenuItem[] = [
  { title: 'Home', path: '/' },
  {
    title: 'About',
    path: '/about',
    megaMenu: {
      sections: [
        {
          title: 'Company',
          items: [
            {
              title: 'About Us',
              description: 'Learn about our company and values',
              path: '/about'
            },
            {
              title: 'Why Choose Us',
              description: 'What sets us apart',
              path: '/why-choose-us'
            }
          ]
        },
        {
          title: 'Company Info',
          items: [
            {
              title: 'Our Process',
              description: 'How we deliver exceptional results',
              path: '/our-process'
            },
            {
              title: 'Contact Us',
              description: 'Get in touch with our team',
              path: '/contact'
            }
          ]
        }
      ]
    }
  },
  {
    title: 'Services',
    path: '/services',
    megaMenu: {
      sections: [
        {
          title: 'Core Services',
          items: [
            {
              title: 'Web Design',
              description: 'Beautiful, responsive websites that convert',
              path: '/services/web-design'
            },
            {
              title: 'Full-Stack Development',
              description: 'End-to-end web application solutions',
              path: '/services/full-stack-development'
            },
            {
              title: 'Website Maintenance',
              description: 'Keep your site secure and up-to-date',
              path: '/services/maintenance'
            }
          ]
        },
        {
          title: 'Specialized Services',
          items: [
            {
              title: 'Technical SEO',
              description: 'Improve search engine visibility',
              path: '/services/technical-seo'
            },
            {
              title: 'E-commerce Solutions',
              description: 'Build your online store',
              path: '/services/ecommerce'
            },
            {
              title: 'Web Applications',
              description: 'Custom web app development',
              path: '/services/web-applications'
            }
          ]
        }
      ]
    }
  },
  {
    title: 'Articles',
    path: '/advice',
    megaMenu: {
      sections: [
        {
          title: 'Articles',
          items: [] // This will be populated dynamically in MegaMenu component
        }
      ]
    }
  },
  { title: 'Contact', path: '/contact' }
];

const Header: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeMegaMenu, setActiveMegaMenu] = useState<string | null>(null);
  const headerRef = useRef<HTMLElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    const handleClickOutside = (event: MouseEvent) => {
      if (headerRef.current && !headerRef.current.contains(event.target as Node)) {
        setActiveMegaMenu(null);
      }
    };

    window.addEventListener('scroll', handleScroll);
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [scrolled]);

  const toggleMenu = () => setIsOpen(!isOpen);
  const closeMenu = () => {
    setIsOpen(false);
    setActiveMegaMenu(null);
  };

  const handleMouseEnter = (title: string) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setActiveMegaMenu(title);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setActiveMegaMenu(null);
    }, 300);
  };

  return (
    <header
      ref={headerRef}
      className={`fixed w-full z-50 transition-all duration-300 ${
        scrolled || activeMegaMenu
          ? 'bg-white shadow-md py-3'
          : 'bg-transparent py-5'
      }`}
    >
      <div className="container-custom flex justify-between items-center relative">
        <Link to="/" className="z-50" onClick={closeMenu}>
          <Logo />
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:block">
          <ul className="flex space-x-8">
            {menuItems.map((item) => (
              <li
                key={item.path}
                className="relative"
                onMouseEnter={() => item.megaMenu && handleMouseEnter(item.title)}
                onMouseLeave={handleMouseLeave}
              >
                <NavLink
                  to={item.path}
                  className={({ isActive }) =>
                    `nav-link text-secondary hover:text-primary flex items-center ${
                      isActive ? 'text-primary' : ''
                    }`
                  }
                  onClick={closeMenu}
                >
                  {item.title}
                  {item.megaMenu && (
                    <ChevronDown size={16} className="ml-1" />
                  )}
                </NavLink>

                <AnimatePresence>
                  {item.megaMenu && activeMegaMenu === item.title && (
                    <MegaMenu
                      sections={item.megaMenu.sections}
                      onClose={() => setActiveMegaMenu(null)}
                    />
                  )}
                </AnimatePresence>
              </li>
            ))}
          </ul>
        </nav>

        <Link to="/quote" className="hidden md:block btn-primary">
          Get a Quote
        </Link>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden z-50 text-secondary"
          onClick={toggleMenu}
          aria-label={isOpen ? 'Close menu' : 'Open menu'}
        >
          {isOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: '100vh' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 bg-white z-40 flex flex-col justify-start md:hidden overflow-y-auto pt-20"
          >
            <nav className="flex flex-col items-start w-full px-6">
              <ul className="w-full">
                {menuItems.map((item) => (
                  <li key={item.path} className="py-4 border-b border-gray-100">
                    <NavLink
                      to={item.path}
                      className={({ isActive }) =>
                        `text-xl ${
                          isActive ? 'text-primary' : 'text-secondary'
                        } hover:text-primary block`
                      }
                      onClick={closeMenu}
                    >
                      {item.title}
                    </NavLink>
                    {item.megaMenu && (
                      <div className="mt-2 ml-4 space-y-2">
                        {item.megaMenu.sections.map((section) => (
                          <div key={section.title} className="mb-4">
                            <h3 className="text-sm font-semibold text-gray-500 mb-2">
                              {section.title}
                            </h3>
                            {section.items.map((subItem) => (
                              <Link
                                key={subItem.path}
                                to={subItem.path}
                                className="block text-secondary hover:text-primary py-1"
                                onClick={closeMenu}
                              >
                                {subItem.title}
                              </Link>
                            ))}
                          </div>
                        ))}
                      </div>
                    )}
                  </li>
                ))}
                <li className="py-4">
                  <Link
                    to="/quote"
                    className="btn-primary inline-block"
                    onClick={closeMenu}
                  >
                    Get a Quote
                  </Link>
                </li>
              </ul>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;